
import {
  defineComponent, ref, reactive, nextTick, watch, onMounted, onUnmounted,
} from 'vue';
import * as echarts from 'echarts';
import html2canvas from 'html2canvas';
import { downLoad, factorColorRender, windDirection, } from '@/utils/index';

import moment from 'moment';

export default defineComponent({
  props: {
    airType: {
      type: String,
      default: () => 'pm25',
      require: true,
    },
    standardDataList: {
      type: Object,
      default: () => ({ lineMap: [], }),
      require: true,
    },
  },
  setup (props) {
    interface chartOption {
      xAxis: any;
      yAxis: any;
      series: any;
      dataZoom?: any[];
    }

    // 要素对应文字
    const factorKey = {
      pm25: 'PM₂.₅',
      pm10: 'PM₁₀',
      so2: 'SO₂',
      no2: 'NO₂',
      o3: 'O₃',
      co: 'CO',
    };

    // 柱状图配置项
    const option: chartOption = reactive({
      xAxis: {
        type: 'category',
        data: [],
        axisLabel: {
          inside: false,
          textStyle: {
            color: '#111',
            fontSize: '14',
            itemSize: '',
          },
          // formatter: (param: any) => `${moment(param).format('MM/DD')}`,
        },
        axisTick: {
          show: false,
        },
        axisPointer: {
          show: true,
          type: 'shadow',
          label: {
            show: false,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#DBDBDB',
          },
        },

        // splitLine: { show: false, },
      },
      legend: {
        itemGap: 30,
        data: [
          'PM2.5',
          'PM10',
          'SO2',
          'NO2',
          'O3',
          'CO',
        ],
      },
      grid: {
        show: false,
      },
      yAxis: [{
        type: 'value',
        name: 'μg/m³',
        position: 'left',
        offset: 60,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#DBDBDB',
          },
        },
        axisLabel: {
          inside: false,
          textStyle: {
            color: '#000',
            fontSize: '14',
            itemSize: '',
          },
        },

        splitLine: {
          show: false,
        },
      },
      {
        type: 'value',
        name: 'mg/m³',
        position: 'right',
        alignTicks: true,
        offset: 60,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#DBDBDB',
          },
        },
        axisLabel: {
          inside: false,
          textStyle: {
            color: '#16C585',
            fontSize: '14',
            itemSize: '',
          },
        },
        splitLine: {
          show: false,
        },
      },],
      tooltip: {
        show: true,
        trigger: 'axis',
        textStyle: {
          align: 'left',
          fontSize: '16',
        },
      },
      series: [
        {
          name: 'PM2.5',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          data: [],
          tooltip: {
            valueFormatter: (value: any) => `${value}ug/m³`,
            formatter: (params: any, ticket: string): any => `<div>${params.name}</div><div><span>${params.marker}</span><span>${params.seriesName}</span><span style='margin-left: 10px'>${params.value}ug/m³</span></div>`,
          },
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#FFA145',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#FFA145',
          },
        },
        {
          name: 'PM10',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#5BC5FF',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#5BC5FF',
          },
          data: [],
          tooltip: {
            valueFormatter: (value: number) => `${value}ug/m³`,
            formatter: (params: any, ticket: string): any => `<div>${params.name}</div><div><span>${params.marker}</span><span>${params.seriesName}</span><span style='margin-left: 10px'>${params.value}ug/m³</span></div>`,
          },
        },
        {
          name: 'SO2',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#FF89AB',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#FF89AB',
          },
          data: [],
          tooltip: {
            valueFormatter: (value: any) => `${value}ug/m³`,
            formatter: (params: any, ticket: string) => `<div>${params.name}</div><div>SO2：${params.value}ug/m³</div>`,
          },
        },
        {
          name: 'NO2',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          data: [],
          tooltip: {
            valueFormatter: (value: any) => `${value}ug/m³`,
            formatter: (params: any, ticket: string): any => `<div>${params.name}</div><div><span>${params.marker}</span><span>${params.seriesName}</span><span style='margin-left: 10px'>${params.value}ug/m³</span></div>`,
          },
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#DBAFFE',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#DBAFFE',
          },
        },
        {
          name: 'O3',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#82CE84',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#82CE84',
          },
          data: [],
          tooltip: {
            valueFormatter: (value: number) => `${value}ug/m³`,
            formatter: (params: any, ticket: string): any => `<div>${params.name}</div><div><span>${params.marker}</span><span>${params.seriesName}</span><span style='margin-left: 10px'>${params.value}ug/m³</span></div>`,
          },
        },
        {
          name: 'CO',
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            color: '#4B63F6',
            width: 2.5,
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            color: '#4B63F6',
          },
          data: [],
          tooltip: {
            valueFormatter: (value: any) => `${value}mg/m³`,
            formatter: (params: any, ticket: string) => `<div>${params.name}</div><div>CO：${params.value}mg/m³</div>`,
          },
        },
      ],
      dataZoom: [
        {
          show: true,
          start: 0,
          end: 100,
        },
      ],
    });
    const analysisChart = ref<any>(null);
    const noDataShow = ref<boolean>(false);

    let chart:any = null;

    /**
     *  配置线状图
     * @param {any} val 配置项
     * @return {void}
     */
    const setChart = (val: any) => {
      nextTick(() => {
        if (val && Object.keys(chart).length) {
          // 不和之前的option合并
          chart.setOption(val, true);
        }
      });
    };
    // 处理X轴数据
    const dealOptionData = (data: any) => {
      const xAxisData:any[] = [];

      if (data && data.length > 0) {
        data.forEach((item:any) => {
          xAxisData.push(moment(item.issue, 'YYYYMMDDHHmm').format('YYYY-MM-DD HH:mm'));
        });
      }
      option.xAxis.data = xAxisData;
    };

    const drawChart = (data:any):void => {
      option.series[0].data = data.pm25;
      option.series[1].data = data.pm10;
      option.series[2].data = data.so2;
      option.series[3].data = data.no2;
      option.series[4].data = data.o3;
      option.series[5].data = data.co;

      setChart(option);
    };

    const computeChartData = (data:any) => {
      const pm25: any[] = [];
      const pm10: any[] = [];
      const so2: any[] = [];
      const no2: any[] = [];
      const o3: any[] = [];
      const co: any[] = [];

      if (data && data.length > 0) {
        data.forEach((item:any) => {
          pm25.push(item.pm25);
          pm10.push(item.pm10);
          so2.push(item.so2);
          no2.push(item.no2);
          o3.push(item.o3);
          co.push(item.co);
        });
      }

      return {
        pm25, pm10,so2,no2,o3,co,
      };
    };
    //

    watch(() => props.standardDataList, (val) => {
      if (val && val.length) {
        noDataShow.value = false;
        const chartData = computeChartData(val);

        dealOptionData(val);
        drawChart(chartData);
      } else {
        noDataShow.value = true;
      }
    },{
      deep: true,
      immediate: true,
    });

    watch(() => props.airType, (cur: any) => {
      if (cur) {
        setTimeout(() => {
          drawChart(props.standardDataList);
        });
      }
    });

    /**
     *  重置图表
     * @param {any} val value
     * @return {void}
     */
    const reSize = (): any => {
      if (Object.keys(chart).length) {
        chart.resize();
      }
    };

    /**
     * 初始化图表
     * @returns {void}
     */
    const initCharts = () => {
      const chartContainer = document.getElementById('chart-container') as HTMLElement;

      chart = echarts.init(chartContainer);
      window.addEventListener('resize', reSize);
    };

    /**
     *  下载图表
     * @return {void}
     */
    const downloadChart = (): any => {
      if (analysisChart.value) {
        html2canvas(analysisChart.value, {
          width: analysisChart.value.clientWidth,
          height: analysisChart.value.clientHeight,
          scrollY: 0,
          scrollX: 0,
          scale: 0.8,
          useCORS: true,
          allowTaint: false,
        }).then((canvas) => {
          const base64 = canvas.toDataURL('image/png');

          downLoad(base64, `${props.airType.toUpperCase()}浓度与气象分析图`);
        });
      }
    };

    /**
     * 挂载图表
     */
    onMounted(() => {
      initCharts();
    });

    /**
     * 销毁图表
     */
    onUnmounted(() => {
      window.removeEventListener('resize', reSize);

      echarts.dispose(chart);
      chart = null;
    });

    return {
      props,
      factorKey,
      noDataShow,
      analysisChart,
      downloadChart,
    };
  },
});
